import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Download, Github, Sparkles, Palette, Search, Layers, 
  Wrench, Shield, ChevronDown, ExternalLink, X, ChevronLeft, ChevronRight,
  Home, HardDrive, Activity, RefreshCw, Gauge,
  Tv, Paintbrush, Puzzle, Gamepad2
} from "lucide-react";
import "@/NeoLibLanding.css";

const DOWNLOAD_URL = "https://github.com/fufugis/Neo-Lib-Launcher/releases/latest/download/NEO-LIB-Setup.exe";
const GITHUB_URL = "https://github.com/fufugis/Neo-Lib-Launcher";

// Google Ads conversion — fires on Latest Release / GitHub CTA clicks.
// No-ops silently if gtag has not loaded (ad-blockers, dev mode, etc.).
const trackDownloadConversion = () => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-18366917110/zXb8CIXjyPEcEPbTg7ZE",
        value: 1.0,
        currency: "NOK",
      });
    }
  } catch (err) {
    // Navigation must always proceed — swallow tracking errors so the download link never breaks.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[neo-lib] conversion tracking skipped:", err);
    }
  }
};

const gallerySections = [
  {
    title: "One home for every launcher",
    subtitle: "One-click import from Steam, Epic, EA, GOG, Ubisoft, Battle.net, Riot, Xbox, Rockstar and itch.io. Deep-scan any drive for standalone games. Fix wrong artwork or metadata in seconds — no re-scans, no rebuilds.",
    shots: [
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/883pdf6o_Skjermbilde%202026-06-20%20000904.webp",
        alt: "NEO-LIB unified library showing Aloft game with cover art, description, screenshots and category sidebar",
        caption: "Unified library — every launcher, every standalone, one interface"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/l9m2mr6h_Skjermbilde%202026-09-05%20194254.png",
        alt: "NEO-LIB Auto-Import Wizard with launcher buttons for Steam, Epic, GOG, EA, Ubisoft, Battle.net, Riot, Xbox, Rockstar and itch.io plus folder-scan configuration",
        caption: "Auto-Import Wizard — pick launchers or scan any drive with skip-paths & scan depth"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/5p0wrqzd_Skjermbilde%202026-09-04%20184412.webp",
        alt: "NEO-LIB Cover Wall showing a large 7x7 grid of resizable game cover art",
        caption: "Cover Wall — resizable big-visual grid, one click to launch, hidden shelves stay hidden"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/bw1j1yze_Skjermbilde%202026-09-05%20184505.png",
        alt: "NEO-LIB Troubleshoot Game dialog for World of Warcraft showing missing screenshots warning and re-fetch buttons for icon, description, screenshots and banner",
        caption: "Troubleshoot Game — spot missing art, re-fetch individual fields, or manually match"
      }
    ]
  },
  {
    title: "Your Home hub — the game-life dashboard",
    subtitle: "Home tracks everything worth tracking: recent sessions, weekly playtime, your Top 5 played, five highest-rated titles, patch notes across your library, new releases worth noticing, library health, storage, gaming chronicle and smart 'what should I play?' nudges — all in one scroll.",
    shots: [
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/gnn1893x_Skjermbilde%202026-09-05%20194207.webp",
        alt: "NEO-LIB Home hero with Top 5 played, weekly at-a-glance stats, private-locked notice and weekly game news carousel",
        caption: "Home hero — Top 5 played, weekly stats, patch notes across your library"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/g8ndlg9i_Skjermbilde%202026-09-05%20193353.webp",
        alt: "NEO-LIB Play and History section showing Recent Sessions, What Should I Play recommendations, My Best Games top 5 and Gaming Chronicle timeline",
        caption: "Play & History — sessions, smart 'what should I play?' nudges and a chronicle timeline"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/g4sv4u21_Skjermbilde%202026-09-05%20193417.webp",
        alt: "NEO-LIB Released This Week popular Steam releases fallback plus Library and PC Care section with 100 percent Library Health and Storage Control Centre",
        caption: "Released This Week + Library Health at 100% + Storage Control Centre"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/t75dfcsc_Skjermbilde%202026-09-05%20193428.webp",
        alt: "NEO-LIB News and Updates section showing Game Updates cards for many games with launcher manifests and safe local-version checks including inZOI new update",
        caption: "News & Updates — launcher manifests plus safe local-version checks for standalone games"
      }
    ]
  },
  {
    title: "Library care & PC power-up",
    subtitle: "NEO-LIB keeps 100+ installs tidy without babysitting. Storage Control Centre shows exactly what each install and mod folder is eating (read-only, safe). Library Health flags missing art, tags and launch targets. Optimize Centre finds real bottlenecks, reviews Windows gaming settings and safely recycles junk files. When you launch a game, Rest Mode drops NEO-LIB to near-zero CPU/RAM so it never steals frames.",
    shots: [
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/z3x6ym2c_Skjermbilde%202026-09-01%20190858.png",
        alt: "NEO-LIB Storage Control Centre showing 109 folders measured, 2159 GB total, 162 GB mod content and per-game folder sizes",
        caption: "Storage Control Centre — see exactly what every install and mod folder is eating"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/sqdw785y_Skjermbilde%202026-09-01%20190801.webp",
        alt: "NEO-LIB Library Health dashboard at 98 percent ready with Art Ready, Details, Launch Targets and Tagged progress",
        caption: "Library Health — one glance shows missing art, tags and launch targets across your whole library"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/r1kf378u_Skjermbilde%202026-09-04%20213848.png",
        alt: "NEO-LIB Optimize Centre showing Prepare Windows for play with Speed Up Gaming CPU RAM GPU inspection and Find Junk and Leftovers safe storage review",
        caption: "Optimize Centre — inspect CPU/RAM/GPU pressure, review Windows gaming settings, recycle safe junk"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/thwhnajq_Skjermbilde%202026-09-05%20194230.png",
        alt: "NEO-LIB Game Ready footer widget showing CPU Low at 20 percent and RAM Low at 48 percent with Rest Mode note",
        caption: "Game Ready + Rest Mode — live CPU/RAM check that hushes NEO-LIB while you play"
      }
    ]
  },
  {
    title: "Personalise every pixel + lock your private shelf",
    subtitle: "14 themes across Bright, Mid, Dark and Special palettes — each with animated particles you can dial down. Sliders control row size, glow intensity, motion rate, category markers and 11 background textures. Font swaps and a Cursive/Fat toggle live-preview instantly. And when the family shares the PC, hide entire categories behind a 4-digit PIN — locked shelves stay invisible until unlocked.",
    shots: [
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/eicgqdr7_Skjermbilde%202026-09-05%20194900.png",
        alt: "NEO-LIB Visuals panel showing Object Sizes sliders, Text and Category font options, FX category glow at 158 percent, effects intensity, visual motion rate, background texture picker with 11 textures and column layout selector",
        caption: "Visuals panel — deep sliders for size, glow, motion, texture, font and layout"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/hip7er4g_Skjermbilde%202026-09-05%20194909.png",
        alt: "NEO-LIB Theme picker settings dialog showing all 14 themes categorised as Bright, Mid, Dark and Special with a Special theme decoration intensity slider",
        caption: "14 themes — Bright, Mid, Dark and Special, with a decoration intensity slider"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/u4vya189_Skjermbilde%202026-09-05%20194152.png",
        alt: "NEO-LIB Unlock Category dialog asking for a 4-digit PIN to reveal games in a private category named Cool",
        caption: "Private + PIN — hide entire categories behind a 4-digit PIN, perfect for shared PCs"
      },
      {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_boring-kilby-5/artifacts/d3up2mvj_Skjermbilde%202026-06-20%20002438.webp",
        alt: "NEO-LIB Ghost private category active with Uncategorized Steam games in dark synthwave theme",
        caption: "A theme in the wild — Ghost mode's synthwave dark with a hidden Private shelf on top"
      }
    ]
  }
];

const screenshots = gallerySections.flatMap(s => s.shots);

const features = [
  { icon: Layers, title: "One Unified Library", desc: "Steam · Epic · GOG · EA · Ubisoft · Battle.net · Riot · Xbox · itch.io · folders · standalone .exes — all in one place" },
  { icon: Home, title: "Home Hub + Widgets", desc: "Recent sessions, Top 5 played, highest-rated, live news, Library Health, Chronicle. Drag, resize and arrange widgets freely with Snap or Free mode" },
  { icon: Shield, title: "Private + PIN Lock", desc: "Hide sensitive shelves behind a 4-digit PIN. Locked rows stay invisible until unlocked" },
  { icon: Palette, title: "14 Dynamic Themes", desc: "Bright · Mid · Dark · Special — Vaporwave Day, Synthwave, Crimson, Daybreak, Magical, Industrial, Anime and more. Each with animated particles you can dial down" },
  { icon: Search, title: "Smart Metadata Recovery", desc: "Covers, hero art, screenshots, descriptions, genres and tags — with repair and AI-assisted paths when matching fails" },
  { icon: HardDrive, title: "Storage Control Centre", desc: "See exactly what every install and mod folder is using. Read-only, safe, launcher-agnostic" },
  { icon: Activity, title: "Library Health", desc: "One dashboard for missing art, details, launch targets and tags across your entire library" },
  { icon: RefreshCw, title: "Update Intelligence", desc: "Checks launcher-managed games and investigates standalone installs with evidence tracking — separates confirmed from 'needs evidence'" },
  { icon: Wrench, title: "Game Care Tools", desc: "Save-folder backups, Launch Doctor, duplicate cleanup, Optimize Centre and per-game repair paths" },
  { icon: Gauge, title: "Game Ready + Rest Mode", desc: "CPU/RAM monitor, dramatic activity drop while a game runs, Windows gaming setting checks" },
  { icon: Sparkles, title: "Fungist Oracle", desc: "Reactive mascot with voice, inbox, chat, library-aware picks and safe commands like 'Launch a random RPG'" },
  { icon: Shield, title: "100% Offline & Private", desc: "No accounts. No cloud. No telemetry. Internet only when YOU actively ask for it" },
  { icon: Tv, title: "NEO Lounge — Big-Screen TV Mode", desc: "A dedicated 10-foot interface with controller focus, scene themes (Steampunk, Anime Winter and more), ambient FX, movable widget areas, Far-mode readability — made for the couch" },
  { icon: Paintbrush, title: "Theme Creator Lab", desc: "Remix any built-in theme in a dedicated artwork workbench — swap button frames, layers, particles, FX styles. Canva and ChatGPT Images round-trip shortcuts, zero uploads from your app" },
  { icon: Puzzle, title: "Plugins & Addons", desc: "Opt-in coded modules with reviewed permissions, isolated pages and clean recoverable removal. Build it, drop it in, keep your library sacred" },
  { icon: Gamepad2, title: "Retro Library + Emulators", desc: "A full Retro Library with console carousel and emulator browsing. Optional Skraper exports, ScreenScraper identification and your own RomM server. You bring the ROMs/emulators — NEO-LIB never bundles them" }
];

const faqs = [
  { q: "What are the system requirements?", a: "Windows 10 or 11 (64-bit). Runs on modest hardware — if it can run any of your game launchers, it runs NEO-LIB. About 200MB disk space for the installer." },
  { q: "Is NEO-LIB really free?", a: "Yes, completely free. Development is funded voluntarily through affiliate-tagged deal links (transparent — you don't pay extra) and optional PayPal donations. Nothing in the app is gated behind payment." },
  { q: "What is NEO Lounge?", a: "A dedicated big-screen / TV mode designed for the couch — large covers, controller focus, ambient scene themes (Steampunk, Anime Winter and more), movable widget areas, Near / Medium / Far readability modes and its own Sound and Visuals panels. Switch between Cover Wall and Game Browser shelf. Covers only open Preview — never launches a game by accident." },
  { q: "Can I build my own themes and plugins?", a: "Yes. The Theme Creator Lab lets you remix any built-in theme — swap button frames, layers, particles and FX styles, with Canva and ChatGPT Images round-trip shortcuts. Plugins are opt-in coded addons / custom modules with reviewed permissions, isolated pages and clean recoverable removal." },
  { q: "Does NEO-LIB support emulators and ROMs?", a: "Yes — the Retro Library has a full console carousel and emulator browsing, optional Skraper exports, ScreenScraper identification and support for your own RomM server. NEO-LIB never bundles ROMs, BIOS or emulators — you bring your own and review every import." },
  { q: "Will Steam achievements, cloud saves and overlays still work?", a: "Yes. NEO-LIB launches games via their original executable, so all platform features (Steam overlays, Epic achievements, EA cloud saves, DRM) keep working exactly as before. NEO-LIB just sits on top as the front door." },
  { q: "Where are my settings and library stored?", a: "Everything lives at %APPDATA%\\NEO-LIB\\ on your PC. Delete that folder to factory-reset. Nothing is stored in the cloud — your library is yours." },
  { q: "Where do I report bugs or request features?", a: "Open an issue on GitHub. The project is actively maintained and every report gets triaged." },
];

const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div 
      className="neo-feature-card"
      data-testid={`feature-card-${index}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <div className="neo-feature-number">{String(index + 1).padStart(2, '0')}</div>
      <div className="neo-feature-icon"><Icon strokeWidth={1.5} /></div>
      <h3 className="neo-feature-title">{feature.title}</h3>
      <p className="neo-feature-desc">{feature.desc}</p>
    </motion.div>
  );
};

const FAQItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`neo-faq-item ${open ? 'open' : ''}`} data-testid={`faq-${index}`}>
      <button 
        className="neo-faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span>{faq.q}</span>
        <ChevronDown className={`neo-faq-chevron ${open ? 'open' : ''}`} strokeWidth={2} />
      </button>
      <div className="neo-faq-answer"><p>{faq.a}</p></div>
    </div>
  );
};

const Lightbox = ({ index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const shot = screenshots[index];
  return (
    <motion.div 
      className="neo-lightbox"
      onClick={onClose}
      data-testid="neo-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <button 
        className="neo-lightbox-close" 
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close lightbox"
      >
        <X strokeWidth={2} />
      </button>
      <button 
        className="neo-lightbox-nav neo-lightbox-prev" 
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev"
        aria-label="Previous screenshot"
      >
        <ChevronLeft strokeWidth={2} />
      </button>
      <button 
        className="neo-lightbox-nav neo-lightbox-next" 
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next"
        aria-label="Next screenshot"
      >
        <ChevronRight strokeWidth={2} />
      </button>
      <motion.div 
        className="neo-lightbox-content"
        onClick={(e) => e.stopPropagation()}
        key={index}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <img src={shot.src} alt={shot.alt} loading="eager" />
        <div className="neo-lightbox-caption">
          <span className="neo-lightbox-counter">{index + 1} / {screenshots.length}</span>
          <span>{shot.caption}</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

function NeoLibLanding() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    document.title = "NEO-LIB — Every game on your PC in one neon library";
    
    const setMeta = (name, content, property = false) => {
      let el = document.querySelector(property ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (property) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    
    setMeta('description', 'NEO-LIB unifies Steam, Epic, EA, GOG and standalone games into one synthwave-flavored Windows launcher. Free, offline, no telemetry. Download the latest release.');
    setMeta('og:title', 'NEO-LIB — Every game on your PC in one neon library', true);
    setMeta('og:description', 'Unify Steam, Epic, EA, GOG and standalone games into one synthwave Windows launcher. Free & offline.', true);
    setMeta('og:type', 'website', true);
    setMeta('og:image', screenshots[0].src, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'NEO-LIB — Every game on your PC in one neon library');
    setMeta('twitter:image', screenshots[0].src);
    
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'neolib-ld';
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NEO-LIB",
      "description": "A synthwave-flavored, fully portable Windows game library that unifies Steam, Epic, EA, GOG and standalone games into one interface. Free, offline, no telemetry.",
      "applicationCategory": "GameApplication",
      "operatingSystem": "Windows 10, Windows 11",
      "softwareVersion": "1.8.6",
      "downloadUrl": DOWNLOAD_URL,
      "screenshot": screenshots.map(s => s.src),
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "author": { "@type": "Person", "name": "fufugis", "url": "https://github.com/fufugis" }
    });
    const existing = document.getElementById('neolib-ld');
    if (existing) existing.remove();
    document.head.appendChild(ldScript);
    
    return () => {
      const el = document.getElementById('neolib-ld');
      if (el) el.remove();
    };
  }, []);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevShot = () => setLightboxIndex((i) => (i - 1 + screenshots.length) % screenshots.length);
  const nextShot = () => setLightboxIndex((i) => (i + 1) % screenshots.length);

  return (
    <main className="neo-lib-page" data-testid="neolib-landing">
      <div className="neo-grid-bg" />
      <div className="neo-scanlines" />
      
      <section className="neo-hero" data-testid="neo-hero">
        <div className="neo-hero-inner">
          <motion.div 
            className="neo-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="neo-hero-badges"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="neo-badge neo-badge-status">● ACTIVE</span>
              <span className="neo-badge neo-badge-platform">Windows x64</span>
              <span className="neo-badge neo-badge-version">v1.8.6</span>
            </motion.div>
            
            <motion.h1 
              className="neo-hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              NEO<span className="neo-title-dash">-</span>LIB
            </motion.h1>
            
            <motion.p 
              className="neo-hero-tagline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              Every game on your PC. One neon-lit library.
            </motion.p>
            
            <motion.p 
              className="neo-hero-sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Steam · Epic · EA App · GOG · standalone — unified. Free, offline, no telemetry.
            </motion.p>
            
            <motion.div 
              className="neo-cta-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <a 
                href={DOWNLOAD_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="neo-btn neo-btn-primary"
                data-testid="hero-download-btn"
                onClick={trackDownloadConversion}
              >
                <Download strokeWidth={2} />
                <span>Download for Windows</span>
              </a>
              <a 
                href={GITHUB_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="neo-btn neo-btn-secondary"
                data-testid="hero-github-btn"
              >
                <Github strokeWidth={2} />
                <span>View on GitHub</span>
              </a>
            </motion.div>
            
            <motion.p 
              className="neo-hero-trust"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Free · No account · No telemetry · Portable available
            </motion.p>
          </motion.div>

          <motion.button
            type="button"
            className="neo-hero-preview"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            onClick={() => openLightbox(0)}
            data-testid="hero-preview-img"
            aria-label="Open NEO-LIB main screenshot fullscreen"
          >
            <img 
              src={screenshots[0].src} 
              alt={screenshots[0].alt}
              loading="eager"
              width="900"
              height="506"
            />
            <div className="neo-hero-preview-glow" />
          </motion.button>
        </div>
      </section>

      <section className="neo-gallery" data-testid="neo-gallery">
        <div className="neo-container">
          <div className="neo-section-header">
            <div className="neo-section-label">// SCREENSHOTS</div>
            <h2 className="neo-section-title">See it in action</h2>
            <p className="neo-section-desc">Click any shot to open fullscreen. Arrow keys to navigate.</p>
          </div>

          {gallerySections.map((section, sIdx) => {
            const startIdx = gallerySections.slice(0, sIdx).reduce((sum, s) => sum + s.shots.length, 0);
            const gridClass = section.shots.length >= 5
              ? 'neo-gallery-grid-5'
              : section.shots.length === 4
                ? 'neo-gallery-grid-4'
                : 'neo-gallery-grid-3';
            return (
              <motion.div
                key={section.title}
                className="neo-gallery-section"
                data-testid={`gallery-section-${sIdx}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
              >
                <div className="neo-gallery-section-header">
                  <h3 className="neo-gallery-section-title">{section.title}</h3>
                  <p className="neo-gallery-section-sub">{section.subtitle}</p>
                </div>
                <div className={`neo-gallery-grid ${gridClass}`}>
                  {section.shots.map((shot, i) => {
                    const globalIdx = startIdx + i;
                    return (
                      <button
                        key={globalIdx}
                        type="button"
                        className={`neo-screenshot ${i === 0 ? 'neo-screenshot-featured' : ''}`}
                        onClick={() => openLightbox(globalIdx)}
                        data-testid={`screenshot-${globalIdx}`}
                        aria-label={`Open screenshot ${globalIdx + 1}: ${shot.caption}`}
                      >
                        <img
                          src={shot.src}
                          alt={shot.alt}
                          loading="lazy"
                          width="600"
                          height="337"
                        />
                        <div className="neo-screenshot-overlay">
                          <span className="neo-screenshot-caption">{shot.caption}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="neo-about" data-testid="neo-about">
        <div className="neo-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="neo-section-label">// WHAT IT IS</div>
            <h2 className="neo-section-title">A launcher for ALL your games.</h2>
            <div className="neo-about-text">
              <p>Standalone games scattered across your drives get auto-detected and added. Your Steam, Epic, EA and GOG libraries pull in alongside them. You can even drop in tools like GPU-Z or MSI Afterburner so everything launches from one place.</p>
              <p>Games launch via their <strong>original executable</strong> — Steam overlays, Epic achievements, cloud saves and DRM keep working exactly as before. NEO-LIB just sits on top as the front door.</p>
              <p>Hit the couch? Flip into <strong>NEO Lounge</strong> for a controller-ready big-screen view with scene themes and movable widgets. Want a different look? The <strong>Theme Creator Lab</strong> lets you remix any theme. Need something that isn't shipped? Drop in a <strong>plugin or addon</strong>. Running a retro collection? The built-in <strong>Retro Library</strong> handles consoles, emulators and your own ROMs — reviewed, never bundled.</p>
              <p className="neo-about-highlight">No accounts. No cloud. No telemetry. Just your games, your way.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="neo-features" data-testid="neo-features">
        <div className="neo-container">
          <div className="neo-section-header">
            <div className="neo-section-label">// FEATURES</div>
            <h2 className="neo-section-title">Everything under one roof</h2>
            <p className="neo-section-desc">Sixteen features. One clean, offline app. Zero telemetry.</p>
          </div>
          
          <div className="neo-features-grid">
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="neo-cta-block" data-testid="neo-cta-block">
        <div className="neo-container">
          <motion.div
            className="neo-cta-inner"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="neo-cta-title">Ready to unify your library?</h2>
            <p className="neo-cta-sub">One-click installer or portable ZIP. Runs anywhere, even off a USB stick.</p>
            
            <div className="neo-cta-buttons">
              <a 
                href={DOWNLOAD_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="neo-btn neo-btn-primary neo-btn-large"
                data-testid="cta-download-btn"
                onClick={trackDownloadConversion}
              >
                <Download strokeWidth={2} />
                <span>Download Latest Release</span>
              </a>
              <a 
                href={GITHUB_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="neo-btn neo-btn-secondary neo-btn-large"
                data-testid="cta-github-btn"
              >
                <Github strokeWidth={2} />
                <span>Source on GitHub</span>
              </a>
            </div>
            
            <p className="neo-cta-trust">Free · No account · No telemetry · Windows 10/11 x64</p>
          </motion.div>
        </div>
      </section>

      <section className="neo-faq" data-testid="neo-faq">
        <div className="neo-container neo-container-narrow">
          <div className="neo-section-header">
            <div className="neo-section-label">// FAQ</div>
            <h2 className="neo-section-title">Common questions</h2>
          </div>
          
          <div className="neo-faq-list">
            {faqs.map((faq, index) => (
              <FAQItem key={faq.q} faq={faq} index={index} />
            ))}
          </div>
        </div>
      </section>

      <footer className="neo-footer" data-testid="neo-footer">
        <div className="neo-container">
          <div className="neo-footer-content">
            <span>© 2026 NEO-LIB · Made with 💜 in synthwave neon</span>
            <a 
              href={GITHUB_URL}
              target="_blank" 
              rel="noopener noreferrer"
              className="neo-footer-link"
              data-testid="footer-github"
            >
              <Github strokeWidth={2} />
              <span>GitHub</span>
              <ExternalLink strokeWidth={2} className="w-3 h-3" />
            </a>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox 
            index={lightboxIndex}
            onClose={closeLightbox}
            onPrev={prevShot}
            onNext={nextShot}
          />
        )}
      </AnimatePresence>
    </main>
  );
}

export default NeoLibLanding;
